import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { LayoutGroup, motion, useReducedMotion } from "framer-motion";
import { Plus } from "lucide-react";
import Card from "./JobCardItem";
import { ApiItem, Status, toStatus } from "../utils/applicationCards";
import {
  COLUMNS,
  emptyStateFor,
  ColumnDef,
  byUpdateDesc,
  columnFor,
  moveApplication,
  restoreApplication,
  NewApplicationCard,
  STATUS_TAG,
} from "./applicationShared";
import { cn } from "../lib/utils";
import UndoToast, { useUndoToast } from "./UndoToast";
import { ease, spring } from "../lib/motion";

// The Kanban column view of application cards. Used by the Tracker (editable)
// and the archive view (readOnly: no card menu or + buttons, no dragging).

/** Empty state: a title and one line of plain text. Also used by Dream Companies. */
export function EmptyColumn({ title, text }: { title: string; text: React.ReactNode }) {
  return (
    <div className="px-1 py-2">
      <p className="text-[15px] font-semibold text-melt-text1">{title}</p>
      <p className="mt-1 max-w-[26ch] text-[13px] leading-[1.45] text-melt-text2">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------- drag

/** Pointer travel before a press becomes a drag, so clicks still open the card */
const DRAG_THRESHOLD = 6;
/** How long the dragged card takes to glide into place, or back home */
const SETTLE_MS = 280;
const SETTLE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
/** Near the board's left/right edge, scroll toward the hidden column */
const EDGE_PX = 64;
const EDGE_SPEED = 14;


function Column({
  column,
  items,
  onUpdate,
  readOnly,
  animateIn,
  searching,
  onOpen,
  isDropTarget,
  draggingId,
  landingId,
  dropped,
  onCardPointerDown,
}: {
  column: ColumnDef;
  items: ApiItem[];
  onUpdate: () => void;
  readOnly: boolean;
  animateIn: boolean;
  searching: boolean;
  onOpen?: (item: ApiItem) => void;
  isDropTarget: boolean;
  draggingId: string | null;
  landingId: string | null;
  dropped: Record<string, number>;
  onCardPointerDown?: (e: React.PointerEvent<HTMLElement>, item: ApiItem, column: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    // -m/p: room for the drop highlight around the column without shifting it
    <div
      data-col={column.id}
      className={cn(
        "-m-1.5 flex h-[calc(100%+12px)] flex-col gap-3.5 rounded-2xl p-1.5 transition-[background-color,box-shadow] duration-150",
        isDropTarget && "bg-melt-caramel/[.07] shadow-[inset_0_0_0_1.5px_rgba(217,136,41,.35)]"
      )}
    >
      <div className="flex items-center gap-2 pr-2">
        <span className="h-2 w-2 rounded-[3px]" style={{ background: column.color }} />
        <h2 className="text-[13.5px] font-semibold text-melt-text1">{column.title}</h2>
        <span className="font-plex text-xs text-melt-text3">{items.length}</span>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            aria-label={`Add to ${column.title}`}
            className="ml-auto grid h-6 w-6 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
          >
            <Plus size={16} strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* content */}
      {/* -mx/-mt + px/pt: room for a card's hover lift and shadow, which the scroll box would otherwise clip */}
      <motion.div
        layoutScroll
        className="scrollbar-thin -mx-1.5 -mt-1.5 flex-1 min-h-0 overflow-y-auto px-1.5 pb-3 pt-1.5 max-h-[calc(100vh-170px)] md:max-h-none"
      >
        {adding && (
          <NewApplicationCard
            status={column.addStatus}
            onAdded={onUpdate}
            onClose={() => setAdding(false)}
            className={items.length > 0 ? "mb-2.5" : undefined}
          />
        )}
        {items.length === 0 ? (
          !adding && <EmptyColumn {...emptyStateFor(column, { readOnly, searching })} />
        ) : (
          <div className="space-y-2.5">
            {/* No exit animation: with search filtering, fading-out cards overlapped the ones moving in */}
            {items.map((it, i) => {
              const s = toStatus(it.predicted_label) ?? "submitted";
              const generation = dropped[it.id];
              return (
                // layoutId lets a card glide to its new column when its status changes.
                // A dropped card gets a fresh one instead: the dragged copy glides it into
                // place, and it shouldn't also slide over from its old column or fade in.
                <motion.div
                  key={it.id}
                  layoutId={generation ? `card-${it.id}-${generation}` : `card-${it.id}`}
                  layout="position"
                  initial={animateIn && !generation ? { opacity: 0, y: 6 } : false}
                  animate={{ opacity: 1, y: 0, transition: { duration: 0.24, ease, delay: Math.min(i, 8) * 0.03 } }}
                  transition={spring}
                  className={cn(it.id === landingId && "invisible")}
                >
                  <Card
                    item={it}
                    status={s}
                    onUpdate={onUpdate}
                    readOnly={readOnly}
                    onOpen={onOpen}
                    placeholder={it.id === draggingId}
                    onDragPointerDown={onCardPointerDown && ((e) => onCardPointerDown(e, it, column.id))}
                  />
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}

type TrackerBoardProps = {
  data: ApiItem[];
  onUpdate?: () => void;
  /** No + buttons, card menus, editing or dragging (the archive view) */
  readOnly?: boolean;
  /** Fade cards in on mount (first load); off when toggling back from List. */
  animateIn?: boolean;
  /** `data` is filtered by a search, so empty columns say "No matches". */
  searching?: boolean;
  /** Clicking a card opens its emails panel. */
  onOpen?: (item: ApiItem) => void;
};

export default function TrackerBoard({ data, onUpdate = () => {}, readOnly = false, animateIn = true, searching = false, onOpen }: TrackerBoardProps) {
  const boardRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  // The card being dragged (shown as a dashed gap) and the column under the pointer
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<string | null>(null);
  // A dropped card, hidden in its new column while the dragged copy glides onto it
  const [landingId, setLandingId] = useState<string | null>(null);
  // How many times each card was dropped in another column (its layoutId generation)
  const [dropped, setDropped] = useState<Record<string, number>>({});
  // Cards moved to another stage, shown there until the reload comes back
  const [moved, setMoved] = useState<Record<string, Status>>({});
  useEffect(() => setMoved({}), [data]);

  const { toast, show: showToast, hide: hideToast } = useUndoToast();

  /** Write a stage change, with Undo in the toast. `item` is the card as it was before. */
  const commitMove = (item: ApiItem, status: Status) => {
    const before = toStatus(item.predicted_label) ?? "submitted";
    setMoved((m) => ({ ...m, [item.id]: status }));
    moveApplication(item, status)
      .then(() => {
        onUpdate();
        showToast({
          title: `Moved ${item.company} to ${STATUS_TAG[status].label}`,
          sub: item.role || undefined,
          undo: () => {
            hideToast();
            setMoved((m) => ({ ...m, [item.id]: before }));
            restoreApplication(item)
              .then(onUpdate)
              .catch((error) => {
                console.error("Error undoing move:", error);
                showToast({ title: `Couldn't move ${item.company} back`, sub: "Please try again." });
              });
          },
        });
      })
      .catch((error) => {
        console.error("Error moving application:", error);
        setMoved(({ [item.id]: _, ...rest }) => rest);
        showToast({ title: `Couldn't move ${item.company}`, sub: "Please try again." });
      });
  };

  /* Drag a card to another column: a copy follows the pointer (tilting with
     its speed), the card leaves a dashed gap, and the column under the pointer
     lights up. Dropped on another column, the copy glides into the card's new
     spot; anywhere else, it glides back home. Mouse and pen only: on touch the
     board scrolls. */
  const onCardPointerDown = (e: React.PointerEvent<HTMLElement>, item: ApiItem, fromColumn: string) => {
    if (e.pointerType === "touch" || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button, input, textarea, select, a, [role='menuitem']")) return;
    const el = e.currentTarget;
    const start = el.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    const grabX = sx - start.left;
    const grabY = sy - start.top;
    // The card's resting shadow, for the copy to settle into
    const restShadow = getComputedStyle(el).boxShadow;
    let ghost: HTMLElement | null = null;
    let target: string | null = null;
    let lastX = sx;
    let vx = 0;
    let px = sx;
    let py = sy;
    let edgeFrame = 0;

    const place = (x: number, y: number, rot = 0) => {
      ghost!.style.transform = `translate3d(${x - grabX}px, ${y - grabY}px, 0) rotate(${rot}deg)`;
    };

    const columnAt = (x: number, y: number) => {
      const col = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-col]");
      return col && boardRef.current?.contains(col) ? col.dataset.col! : null;
    };

    const updateTarget = () => {
      const next = columnAt(px, py);
      if (next !== target) {
        target = next;
        setOverColumn(next && next !== fromColumn ? next : null);
      }
    };

    // Keep scrolling while the pointer rests near an edge
    const edgeScroll = () => {
      const board = boardRef.current;
      if (board) {
        const r = board.getBoundingClientRect();
        const step = px > r.right - EDGE_PX ? EDGE_SPEED : px < r.left + EDGE_PX ? -EDGE_SPEED : 0;
        if (step) {
          board.scrollLeft += step;
          updateTarget();
        }
      }
      edgeFrame = requestAnimationFrame(edgeScroll);
    };

    const move = (ev: PointerEvent) => {
      px = ev.clientX;
      py = ev.clientY;
      if (!ghost) {
        if (Math.hypot(px - sx, py - sy) < DRAG_THRESHOLD) return;
        ghost = el.cloneNode(true) as HTMLElement;
        ghost.removeAttribute("role");
        ghost.removeAttribute("tabindex");
        Object.assign(ghost.style, {
          position: "fixed",
          left: "0",
          top: "0",
          width: `${start.width}px`,
          margin: "0",
          zIndex: "60",
          pointerEvents: "none",
          boxShadow: "0 26px 44px -20px rgba(92,52,23,.5)",
          willChange: "transform",
          // The card's own hover transition would make the copy trail the pointer
          transition: "none",
        });
        document.body.appendChild(ghost);
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        setDraggingId(item.id);
        edgeFrame = requestAnimationFrame(edgeScroll);
      }
      vx = vx * 0.7 + (px - lastX) * 0.3;
      lastX = px;
      place(px, py, reduceMotion ? 0 : Math.max(-4, Math.min(4, vx * 0.5)));
      updateTarget();
    };

    const glide = (to: DOMRect, done: () => void) => {
      const g = ghost!;
      let settled = false;
      // Show the card first, then drop the copy, so there's no empty frame between
      const settle = () => {
        if (settled) return;
        settled = true;
        flushSync(done);
        g.remove();
      };
      if (reduceMotion) return settle();
      g.animate(
        [
          { transform: g.style.transform, boxShadow: g.style.boxShadow },
          { transform: `translate3d(${to.left}px, ${to.top}px, 0) rotate(0deg)`, boxShadow: restShadow },
        ],
        { duration: SETTLE_MS, easing: SETTLE_EASE, fill: "forwards" }
      ).onfinish = settle;
      // A hidden tab never finishes the animation; don't leave the copy behind
      window.setTimeout(settle, SETTLE_MS + 100);
    };

    const finish = (cancelled: boolean) => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(edgeFrame);
      if (!ghost) return;
      updateTarget();
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setOverColumn(null);
      // The release would also click the card and open its emails
      window.addEventListener("click", stopClick, true);
      window.setTimeout(() => window.removeEventListener("click", stopClick, true), 0);

      const to = cancelled ? null : COLUMNS.find((c) => c.id === target && c.id !== fromColumn);
      if (!to) {
        // Back home along the way it came
        glide(el.getBoundingClientRect(), () => setDraggingId(null));
        return;
      }
      // Render the card in its new column now, so the copy can land on it
      flushSync(() => {
        setDraggingId(null);
        setLandingId(item.id);
        setDropped((d) => ({ ...d, [item.id]: (d[item.id] ?? 0) + 1 }));
        commitMove(item, to.addStatus);
      });
      const landed = boardRef.current?.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(item.id)}"]`);
      landed?.scrollIntoView({ block: "nearest", inline: "nearest" });
      glide(landed?.getBoundingClientRect() ?? start, () => setLandingId(null));
    };
    const stopClick = (ev: MouseEvent) => ev.stopPropagation();
    const up = (ev: PointerEvent) => {
      px = ev.clientX;
      py = ev.clientY;
      finish(false);
    };
    const cancel = () => finish(true);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") finish(true);
    };

    el.setPointerCapture(e.pointerId);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", onKey);
  };

  const byColumn = useMemo(() => {
    const groups: Record<string, ApiItem[]> = {};
    COLUMNS.forEach((c) => (groups[c.id] = []));
    for (const original of data) {
      const it = moved[original.id] ? { ...original, predicted_label: moved[original.id] } : original;
      const col = columnFor(toStatus(it.predicted_label));
      if (col) groups[col].push(it);
    }

    // Newest first in every column
    Object.values(groups).forEach((items) => items.sort(byUpdateDesc));
    return groups;
  }, [data, moved]);

  return (
    // -m/p: room for the columns' drop highlight, which the scroll box would otherwise
    // clip, without changing the columns' size (the Tracker page makes the same room)
    <div ref={boardRef} className="-m-1.5 overflow-x-auto scrollbar-hide p-1.5 font-grotesk md:min-h-0 md:flex-1">
      <LayoutGroup>
        <div className="flex flex-col gap-8 md:h-full md:flex-row md:flex-nowrap md:gap-[18px]">
          {COLUMNS.map((column) => (
            <div key={column.id} className="w-full min-w-0 flex-none md:flex-[0_0_calc((100%_-_54px)_/_4)]">
              <Column
                column={column}
                items={byColumn[column.id]}
                onUpdate={onUpdate}
                readOnly={readOnly}
                animateIn={animateIn}
                searching={searching}
                onOpen={onOpen}
                isDropTarget={overColumn === column.id}
                draggingId={draggingId}
                landingId={landingId}
                dropped={dropped}
                onCardPointerDown={readOnly ? undefined : onCardPointerDown}
              />
            </div>
          ))}
        </div>
      </LayoutGroup>

      <UndoToast toast={toast} />
    </div>
  );
}
